import {
  ClipboardCheckIcon,
  TimerIcon,
  PenLineIcon,
  TriangleAlertIcon,
  ListChecksIcon,
  GraduationCapIcon,
  FootprintsIcon,
  ShieldAlertIcon,
  LayoutTemplateIcon,
  BookOpenCheckIcon,
  HeartPulseIcon,
  type LucideIcon,
} from "lucide-react";

export type IconItem = { icon: LucideIcon; title: string; text: string };

export const beforeItems = [
  "You review different resources whenever you need to revisit an assessment concept.",
  "You worry about overlooking important patient history or risk factors.",
  "You find it difficult to keep sensory, circulatory, and skin findings organized in one place.",
  "You want a more consistent way to document wounds and other foot abnormalities.",
  "You aren't always sure which findings may require further assessment or referral.",
  "You spend additional time organizing notes before reviewing a clinical case.",
];

export const afterItems = [
  "You have six organized volumes covering key assessment topics.",
  "You can use a structured checklist to review relevant assessment components.",
  "You have dedicated references for sensory, circulatory, skin, nail, and wound findings.",
  "You can organize your documentation with supplementary templates.",
  "You can review risk factors and warning signs in a dedicated reference.",
  "You can practice organizing findings through educational case scenarios.",
];

export const situations: IconItem[] = [
  {
    icon: ClipboardCheckIcon,
    title: "You want to make sure you review the important assessment components.",
    text: "You understand that diabetic foot assessment involves several factors, but you would appreciate a structured reference that helps you organize them.",
  },
  {
    icon: TimerIcon,
    title: "You spend too much time searching through different resources.",
    text: "You would rather have relevant assessment concepts organized into a single collection than repeatedly navigate unrelated notes and documents.",
  },
  {
    icon: PenLineIcon,
    title: "You want to improve the way you document clinical findings.",
    text: "You need a practical framework for recording skin changes, wounds, sensory observations, circulatory findings, and other relevant information.",
  },
  {
    icon: TriangleAlertIcon,
    title: "You want to review warning signs more systematically.",
    text: "You recognize the importance of identifying potentially concerning findings and understanding when additional evaluation may be necessary.",
  },
  {
    icon: ListChecksIcon,
    title: "You want a consistent assessment checklist.",
    text: "You would appreciate a structured way to review relevant information and identify areas that still need clarification.",
  },
  {
    icon: GraduationCapIcon,
    title: "You want to reinforce your knowledge through practical examples.",
    text: "Reading about assessment principles is useful, but you also want educational scenarios that help you practice organizing information and reasoning through cases.",
  },
];

export const resources: IconItem[] = [
  {
    icon: ListChecksIcon,
    title: "A Structured Assessment Checklist",
    text: "Review patient history, general inspection, sensory and circulatory findings, skin and nail observations, risk factors, and follow-up considerations in an organized sequence.",
  },
  {
    icon: HeartPulseIcon,
    title: "Sensory and Circulatory Reference Sheets",
    text: "Revisit relevant examination concepts and documentation considerations without having to search through the full collection every time.",
  },
  {
    icon: FootprintsIcon,
    title: "Skin and Wound Observation References",
    text: "Organize descriptive observations related to skin integrity, nail changes, wounds, and surrounding tissue.",
  },
  {
    icon: ShieldAlertIcon,
    title: "Risk Factor and Warning Sign Review Sheets",
    text: "Use dedicated references to revisit factors associated with diabetic foot risk and findings that may warrant additional assessment.",
  },
  {
    icon: LayoutTemplateIcon,
    title: "Clinical Documentation Templates",
    text: "Structure relevant notes, organize observations, and record follow-up considerations using adaptable templates.",
  },
  {
    icon: BookOpenCheckIcon,
    title: "Educational Case Exercises",
    text: "Review example scenarios, identify relevant findings, and practice organizing questions for further assessment.",
  },
];

export const insideHighlights = [
  { lead: "6", title: "Specialized educational volumes", text: "Covering the main components of diabetic foot assessment." },
  { lead: "5", title: "Practical bonuses", text: "Designed to support your review, documentation, and case-based learning." },
  { lead: "icon-checklist", title: "Checklists & templates", text: "To help organize relevant findings." },
  { lead: "icon-cases", title: "Educational case exercises", text: "Practice identifying important information and questions that may require further assessment." },
];

export const assessmentFactors = [
  "Patient history",
  "Protective sensation",
  "Circulation",
  "Skin integrity",
  "Wounds",
  "Footwear",
  "Individual risk factors",
];

export const packageIncludes = [
  "Six focused educational volumes.",
  "Five specialized supplementary resources.",
  "Sensory and circulatory assessment references.",
  "Skin, nail, and wound observation materials.",
  "Risk factor and warning sign references.",
  "Documentation and follow-up templates.",
  "Educational clinical case exercises.",
];

export const collectionHighlights = [
  "Six focused educational volumes.",
  "Five specialized bonuses.",
  "Assessment and documentation references.",
  "Checklists and adaptable templates.",
  "Educational case exercises.",
  "A collection you can revisit for continued learning.",
];

export type Testimonial = { role: string; image: string; quote: string };

export const testimonials: Testimonial[] = [
  {
    role: "Podiatry Professional",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/b6c11b7e-976c-47e0-b16c-aee92de9cc20.jpg",
    quote:
      "What I appreciate about this type of resource is having the main diabetic foot assessment topics organized in one collection. I can review patient history, sensory and circulatory considerations, skin findings, risk factors, and documentation without jumping between unrelated materials. The checklist format is especially useful for organizing what I want to review.",
  },
  {
    role: "Physical Therapy Professional",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/0b63e582-6950-42b6-ad51-f054a9f14d25.jpg",
    quote:
      "The separate volumes make the topics easier to navigate, and I like that the material includes both assessment concepts and documentation considerations. The educational cases also encourage you to think about what information is available and what may still need to be clarified.",
  },
  {
    role: "Healthcare Professional in Training",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/9d389a88-573b-4776-8dd4-4a5dcf765f65.jpg",
    quote:
      "The assessment framework and supporting checklists are the parts I would use most often for studying. They help organize the major areas I need to consider, from patient history and foot inspection to sensory findings and risk factors.",
  },
  {
    role: "Foot Health Professional",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/bbbb1db6-cd20-4b64-a233-9c1efb2e5094.jpg",
    quote:
      "I like that this collection goes beyond skin and wound observations. It also addresses patient history, sensory and circulatory assessment, risk factors, warning signs, and documentation. The supplementary templates provide a useful starting point for my professional setting.",
  },
  {
    role: "Healthcare Professional",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/7160223b-4b3f-4f03-8719-7c7ec3eb9141.jpg",
    quote:
      "The case-based exercises are an interesting addition because they encourage you to organize findings rather than simply read about them. The resource is clearly intended to support learning rather than replace clinical guidelines, which is an important distinction.",
  },
];

export const faqs = [
  {
    q: "How will I receive the material?",
    a: "This is a digital educational product. After completing your purchase, follow the instructions provided by the checkout and delivery system to access the included volumes and bonuses.",
  },
  {
    q: "Can I use this resource in my country?",
    a: "The collection covers general educational concepts related to diabetic foot assessment. However, clinical practice, professional scope, documentation requirements, and referral protocols vary by country and institution. You should always consult the guidelines and standards applicable to your location.",
  },
  {
    q: "Do I need to be a specialist?",
    a: "This resource is intended for qualified healthcare professionals, relevant healthcare trainees under appropriate supervision, and others whose educational or professional role includes diabetic foot assessment. It does not replace formal training, supervised practice, or competency requirements. You should use the material within your professional scope.",
  },
  {
    q: "Are the files downloadable?",
    a: "The product is delivered digitally. Access and download options are provided through the checkout and delivery system after your purchase.",
  },
  {
    q: "Is this a one-time payment?",
    a: "Yes. The offer presented on this page is $27 USD as a one-time payment for the six-volume collection and five bonuses. It is not a monthly subscription. Check the final checkout page for the applicable price, taxes, and any additional charges.",
  },
  {
    q: "What if the material isn't what I expected?",
    a: "Please review the product description carefully before purchasing. Your purchase is covered by our seven-day refund policy: if the resource doesn't meet your expectations, you may request a refund within seven days of purchase, subject to the terms provided at checkout.",
  },
  {
    q: "Does this blueprint replace clinical guidelines?",
    a: "No. The blueprint is an educational reference. It does not replace current evidence-based guidelines, patient-specific assessment, formal training, professional judgment, or institutional protocols.",
  },
  {
    q: "Does the product provide diagnoses or treatment plans?",
    a: "No. It provides educational assessment references, documentation concepts, and case-based exercises. It does not diagnose patients or prescribe patient-specific treatment.",
  },
  {
    q: "What are the five bonuses?",
    a: "Diabetic Foot Quick-Reference Cards, The Diabetic Foot Assessment Checklist, The Foot Findings Differential Reference, Diabetic Foot Clinical Case Practice Pack, and Diabetic Foot Documentation and Follow-Up Template Collection. These resources complement the six main volumes.",
  },
  {
    q: "What is included in the six volumes?",
    a: "The volumes cover the assessment framework, sensory and circulatory assessment, skin and wound findings, risk factors and classification, warning signs and referral considerations, and educational clinical reasoning cases.",
  },
];

export const buyers = [
  { name: "María G.", country: "Mexico" },
  { name: "Carlos R.", country: "Argentina" },
  { name: "Lucía M.", country: "Colombia" },
  { name: "Javier P.", country: "Spain" },
  { name: "Valentina S.", country: "Chile" },
  { name: "Andrés T.", country: "Peru" },
  { name: "Camila F.", country: "Ecuador" },
  { name: "Diego H.", country: "Uruguay" },
  { name: "Gabriela L.", country: "Venezuela" },
  { name: "Fernando C.", country: "Guatemala" },
  { name: "Daniela V.", country: "Costa Rica" },
  { name: "Sofía A.", country: "Dominican Republic" },
  { name: "Ricardo N.", country: "Panama" },
  { name: "Paula E.", country: "Paraguay" },
  { name: "Mateo B.", country: "Bolivia" },
  { name: "Isabel O.", country: "Honduras" },
];

export const footerLinks = [
  { label: "Contact / Customer Support", href: "#" },
  { label: "Terms and Conditions", href: "#" },
  { label: "Privacy Policy", href: "#" },
  { label: "Refund Policy", href: "#" },
];
